import { type FC, useState } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"

import { toDistinctArray } from "@forento/core/utilities/array"
import { emailRegex } from "@forento/core/utilities/regex"

import { UploadFileIcon } from "~/components/Icon"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"

// Windows often reports CSV files as application/vnd.ms-excel, so detect them by extension too
function isCsvFile(file: File) {
	return file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv")
}

async function parseFile(file: File): Promise<string[]> {
	if (isCsvFile(file)) {
		const { parse } = await import("papaparse")
		const result = parse(await file.text(), { skipEmptyLines: "greedy" })
		// Delimiter and field count warnings are expected for single-column files, but quote errors mean rows were misread
		if (result.errors.some(error => error.type === "Quotes")) throw new Error("Malformed CSV file")
		return result.data.map(row => (Array.isArray(row) ? row[0] : ""))
	} else {
		const readXlsxFile = await import("read-excel-file/browser")
		const data = await readXlsxFile.readSheet(file)
		return data.map(row => row.at(0)?.toString() ?? "")
	}
}

type Props = { onEmailsUploaded(emails: string[]): void }
const Uploader: FC<Props> = ({ onEmailsUploaded }) => {
	const alert = useAlert()

	const [isUploading, setUploading] = useState(false)

	const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
		accept: {
			"text/csv": [".csv"],
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
		},
		multiple: false,
		async onDrop(acceptedFiles, fileRejections) {
			if (fileRejections.some(rejection => rejection.errors.some(error => error.code === "too-many-files"))) {
				await alert.show("Too many files", "Please upload one spreadsheet at a time.")
				return
			}
			if (fileRejections.length > 0) {
				await alert.show("Unsupported file", "Please save the file as .xlsx or .csv and try again.")
				return
			}

			const file = acceptedFiles[0]
			if (file === undefined) return

			if (file.size > 400 * 1024 * 1024) {
				await alert.show(
					"File too large",
					"The file is too large. It can be a maximum of 400 MB. Please upload a smaller file.",
				)
				return
			}

			setUploading(true)

			let data: string[]
			try {
				data = await parseFile(file)
			} catch {
				await alert.show(
					"Invalid spreadsheet",
					"The file couldn't be read. Please save the file as .xlsx or .csv and try again.",
				)
				setUploading(false)
				return
			}
			if (data.length === 0) {
				await alert.show("Invalid spreadsheet", "The spreadsheet is empty.")
				setUploading(false)
				return
			}

			const emails = toDistinctArray(data.filter(row => row.length > 0 && emailRegex.test(row)))
			if (emails.length === 0) {
				await alert.show("Invalid spreadsheet", "The spreadsheet doesn't contain any valid emails.")
				setUploading(false)
				return
			}

			setUploading(false)
			onEmailsUploaded(emails)
		},
	})

	return (
		<Dropzone {...getRootProps()}>
			{!isUploading && <input {...getInputProps()} />}
			{isUploading ? (
				<PartialLoadingPage />
			) : isDragActive ? (
				<InfoText>{isDragReject ? "That file type is not supported" : `Drop spreadsheet file here`}</InfoText>
			) : (
				<>
					<Icon as={UploadFileIcon} />
					<InfoText>Click here or drag and drop to upload a spreadsheet</InfoText>
				</>
			)}
		</Dropzone>
	)
}

const Dropzone = styled.div`
	border: 1px dashed ${props => props.theme.primaryColor};
	border-radius: 8px;
	padding: 32px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 32px;
	cursor: pointer;
	position: relative;
`

const Icon = styled.div`
	width: 64px;
	height: 64px;
	color: ${props => props.theme.primaryColor};
`

const InfoText = styled.p`
	font-weight: 500;
	font-size: 16px;
	text-align: center;
`

export default Uploader
